import type { AppEventType } from '../@models/domain-event';

export const NotificationTypes = {
  CHECKIN: 'checkin',
  MEMBER_REQUEST: 'member_request',
  APPOINTMENT: 'appointment',
  BOOKING: 'booking',
  REMINDER: 'reminder',
  ALERT: 'alert',
  UPDATE: 'update',
  MESSAGE: 'message',
  SHOP_SETTINGS: 'shop_settings',
  PROMOTION: 'promotion',
  CONTACT_US: 'contact_us',
  SHOP_RATING: 'shop_rating',
  SHOP_PAYMENT: 'shop_payment',
  USER_PAYMENT: 'user_payment',
  WELCOME: 'welcome',
  SYSTEM: 'system',
  ERROR: 'error',
} as const;

export type NotificationType = (typeof NotificationTypes)[keyof typeof NotificationTypes];

export const NotificationCategory = {
  APPOINTMENT: 'appointment',
  BOOKING: 'booking',
  PAYMENT: 'payment',
  SECURITY: 'security',
  SHOP: 'shop',
  MEMBER: 'member',
  REVIEW: 'review',
  MESSAGE: 'message',
  SYSTEM: 'system',
} as const;

export type NotificationCategoryValue =
  (typeof NotificationCategory)[keyof typeof NotificationCategory];

export const NotificationPriority = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const;

export type NotificationPriorityValue =
  (typeof NotificationPriority)[keyof typeof NotificationPriority];

export const NotificationScopes = {
  USER: 'user',
  SHOP: 'shop',
  SYSTEM: 'system',
} as const;

export type NotificationScopeValue = (typeof NotificationScopes)[keyof typeof NotificationScopes];

export function normalizeNotificationType(eventType: AppEventType | string): NotificationType {
  if (eventType.startsWith('security.')) return NotificationTypes.ALERT;
  if (eventType.startsWith('appointment.')) return NotificationTypes.APPOINTMENT;
  if (eventType.startsWith('payment.') || eventType.startsWith('refund.'))
    return NotificationTypes.SHOP_PAYMENT;
  if (eventType.startsWith('message.')) return NotificationTypes.MESSAGE;
  if (eventType.startsWith('member.')) return NotificationTypes.MEMBER_REQUEST;
  if (eventType.startsWith('review.')) return NotificationTypes.SHOP_RATING;
  if (eventType.startsWith('shop.') || eventType.startsWith('platform.'))
    return NotificationTypes.SHOP_SETTINGS;
  return NotificationTypes.SYSTEM;
}

export function resolveNotificationCategory(
  eventType: AppEventType | string,
): NotificationCategoryValue {
  if (eventType.startsWith('appointment.')) return NotificationCategory.APPOINTMENT;
  if (eventType.startsWith('payment.') || eventType.startsWith('refund.'))
    return NotificationCategory.PAYMENT;
  if (eventType.startsWith('security.')) return NotificationCategory.SECURITY;
  if (eventType.startsWith('member.')) return NotificationCategory.MEMBER;
  if (eventType.startsWith('message.')) return NotificationCategory.MESSAGE;
  if (eventType.startsWith('review.')) return NotificationCategory.REVIEW;
  if (eventType.startsWith('shop.') || eventType.startsWith('platform.'))
    return NotificationCategory.SHOP;
  return NotificationCategory.SYSTEM;
}

export function resolveNotificationPriority(
  eventType: AppEventType | string,
): NotificationPriorityValue {
  if (eventType.startsWith('security.')) return NotificationPriority.CRITICAL;
  if (eventType.startsWith('payment.') || eventType.startsWith('refund.'))
    return NotificationPriority.HIGH;
  if (eventType.startsWith('appointment.') || eventType.startsWith('member.'))
    return NotificationPriority.NORMAL;
  return NotificationPriority.LOW;
}

export function resolveNotificationScope(eventType: AppEventType | string): NotificationScopeValue {
  if (
    eventType.startsWith('appointment.') ||
    eventType.startsWith('shop.') ||
    eventType.startsWith('member.')
  ) {
    return NotificationScopes.SHOP;
  }
  if (eventType.startsWith('message.')) return NotificationScopes.USER;
  if (eventType.startsWith('security.')) return NotificationScopes.SYSTEM;
  return NotificationScopes.USER;
}
